import { Container, Paper, Stack, Text, Title, Button, Group } from "@mantine/core";
import { IconShieldX } from "@tabler/icons-react";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSignupCompletion } from "../../hooks/useSignupCompletion";

interface UnapprovedUserGuardProps {
  children: ReactNode;
}

/**
 * Guards routes for brand users who haven't been approved yet.
 * Shows a restricted access message instead of the actual page content.
 */
function UnapprovedUserGuard({ children }: UnapprovedUserGuardProps) {
  const { userType, isBrandApproved, loading } = useSignupCompletion();

  // Show loading state while checking
  if (loading) {
    return null;
  }

  // Only restrict BRAND_USER who are not approved
  const shouldRestrict = userType === "BRAND_USER" && !isBrandApproved;

  if (shouldRestrict) {
    return (
      <Container size="sm" py="xl" style={{ minHeight: "60vh", display: "flex", alignItems: "center" }}>
        <Paper p="xl" radius="lg" shadow="md" withBorder w="100%">
          <Stack gap="lg" align="center">
            <IconShieldX size={64} color="var(--mantine-color-yellow-6)" />

            <Title order={2} ta="center">
              Account Pending Approval
            </Title>

            <Stack gap="sm">
              <Text size="sm" c="dimmed" ta="center">
                Your brand account needs approval before you can access this feature.
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Please reach out to your brand's account owner or contact our support team for assistance.
              </Text>
            </Stack>

            <Group mt="md">
              <Button
                component={Link}
                to="/signup/complete"
                variant="light"
              >
                View Account Status
              </Button>
              <Button
                component={Link}
                to="/contact"
                variant="outline"
              >
                Contact Support
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Approved or not a brand user - show normal content
  return <>{children}</>;
}

export default UnapprovedUserGuard;
